import type { UserRole } from '../types/users';

// Типы разрешений
export type Permission = 
  // Teams
  | 'teams.view'
  | 'teams.create' 
  | 'teams.edit'
  | 'teams.delete'
  // Players
  | 'players.view'
  | 'players.create'
  | 'players.edit'
  | 'players.delete'
  // Matches
  | 'matches.view'
  | 'matches.create'
  | 'matches.edit'
  | 'matches.delete'
  // Tournaments
  | 'tournaments.view'
  | 'tournaments.create'
  | 'tournaments.edit'
  | 'tournaments.delete'
  // Tournament Categories
  | 'tournament-categories.view'
  | 'tournament-categories.create'
  | 'tournament-categories.edit'
  | 'tournament-categories.delete'
  // Sport Clubs
  | 'sport-clubs.view'
  | 'sport-clubs.create'
  | 'sport-clubs.edit'
  | 'sport-clubs.delete'
  // Sport Types
  | 'sport-types.view'
  | 'sport-types.create'
  | 'sport-types.edit'
  | 'sport-types.delete'
  // Match Participants
  | 'match-participants.view'
  | 'match-participants.create'
  | 'match-participants.edit'
  | 'match-participants.delete'
  // Users
  | 'users.view'
  | 'users.create'
  | 'users.edit'
  | 'users.delete'
  | 'users.manage'
  // Playgrounds
  | 'playgrounds.view'
  | 'playgrounds.create'
  | 'playgrounds.edit'
  | 'playgrounds.delete'
  // Achievements
  | 'achievements.view'
  | 'achievements.create'
  | 'achievements.edit'
  | 'achievements.delete'
  // Regions
  | 'regions.view'
  | 'regions.create'
  | 'regions.edit'
  | 'regions.delete'
  // Cities
  | 'cities.view'
  | 'cities.create'
  | 'cities.edit'
  | 'cities.delete'
  // Countries
  | 'countries.view'
  | 'countries.create'
  | 'countries.edit'
  | 'countries.delete'
  // Files
  | 'files.view'
  | 'files.upload'
  | 'files.delete'
  // News
  | 'news.view'
  | 'news.create'
  | 'news.edit'
  | 'news.delete'
  | 'news.publish'
  // Favorites
  | 'favorites.view'
  | 'favorites.manage'
  // Analytics
  | 'analytics.view'
  | 'analytics.export'
  // Permissions
  | 'permissions.view'
  | 'permissions.manage'
  // League Admins management (ADMIN only)
  | 'league-admins.view'
  | 'league-admins.create'
  | 'league-admins.edit'
  | 'league-admins.delete'
  // My League panel (ADMIN_LEAGUE only)
  | 'my-league.view';

// Роли системы
export type RoleName = 'ADMIN' | 'PLAYER' | 'GUEST' | 'ADMIN_LEAGUE';

type AuthLikeUser = {
  role?: string | string[];
  roles?: UserRole[];
} | null | undefined;

export const dashboardAccessRoles: RoleName[] = [
  'ADMIN',
  'ADMIN_LEAGUE',
];

// Конфигурация разрешений
export const permissions: Record<Permission, RoleName[]> = {
  // Teams
  'teams.view': ['ADMIN', 'GUEST'],
  'teams.create': ['ADMIN'],
  'teams.edit': ['ADMIN'],
  'teams.delete': ['ADMIN'],

  // Players
  'players.view': ['ADMIN', 'GUEST'],
  'players.create': ['ADMIN'],
  'players.edit': ['ADMIN'],
  'players.delete': ['ADMIN'],

  // Matches
  'matches.view': ['ADMIN', 'GUEST'],
  'matches.create': ['ADMIN'],
  'matches.edit': ['ADMIN'],
  'matches.delete': ['ADMIN'],

  // Tournaments
  'tournaments.view': ['ADMIN', 'GUEST'],
  'tournaments.create': ['ADMIN'],
  'tournaments.edit': ['ADMIN'],
  'tournaments.delete': ['ADMIN'],

  // Tournament Categories
  'tournament-categories.view': ['ADMIN', 'GUEST'],
  'tournament-categories.create': ['ADMIN'],
  'tournament-categories.edit': ['ADMIN'],
  'tournament-categories.delete': ['ADMIN'],

  // Sport Clubs
  'sport-clubs.view': ['ADMIN', 'GUEST'],
  'sport-clubs.create': ['ADMIN'],
  'sport-clubs.edit': ['ADMIN'],
  'sport-clubs.delete': ['ADMIN'],

  // Sport Types
  'sport-types.view': ['ADMIN', 'GUEST'],
  'sport-types.create': ['ADMIN'],
  'sport-types.edit': ['ADMIN'],
  'sport-types.delete': ['ADMIN'],

  // Match Participants
  'match-participants.view': ['ADMIN', 'GUEST'],
  'match-participants.create': ['ADMIN'],
  'match-participants.edit': ['ADMIN'],
  'match-participants.delete': ['ADMIN'],

  // Users
  'users.view': ['ADMIN'],
  'users.create': ['ADMIN'],
  'users.edit': ['ADMIN'],
  'users.delete': ['ADMIN'],
  'users.manage': ['ADMIN'],

  // Playgrounds
  'playgrounds.view': ['ADMIN', 'GUEST'],
  'playgrounds.create': ['ADMIN'],
  'playgrounds.edit': ['ADMIN'],
  'playgrounds.delete': ['ADMIN'],

  // Achievements
  'achievements.view': ['ADMIN', 'PLAYER', 'GUEST'],
  'achievements.create': ['ADMIN'],
  'achievements.edit': ['ADMIN'],
  'achievements.delete': ['ADMIN'],

  // Regions
  'regions.view': ['ADMIN', 'GUEST'],
  'regions.create': ['ADMIN'],
  'regions.edit': ['ADMIN'],
  'regions.delete': ['ADMIN'],

  // Cities
  'cities.view': ['ADMIN', 'GUEST'],
  'cities.create': ['ADMIN'],
  'cities.edit': ['ADMIN'],
  'cities.delete': ['ADMIN'],

  // Countries
  'countries.view': ['ADMIN', 'GUEST'],
  'countries.create': ['ADMIN'],
  'countries.edit': ['ADMIN'],
  'countries.delete': ['ADMIN'],

  // Files
  'files.view': ['ADMIN'],
  'files.upload': ['ADMIN'],
  'files.delete': ['ADMIN'],

  // News
  'news.view': ['ADMIN', 'GUEST'],
  'news.create': ['ADMIN'],
  'news.edit': ['ADMIN'],
  'news.delete': ['ADMIN'],
  'news.publish': ['ADMIN'],

  // Favorites
  'favorites.view': ['ADMIN'],
  'favorites.manage': ['ADMIN'],

  // Analytics
  'analytics.view': ['ADMIN'],
  'analytics.export': ['ADMIN'],

  // Permissions
  'permissions.view': ['ADMIN'],
  'permissions.manage': ['ADMIN'],

  // League Admins management (ADMIN only)
  'league-admins.view': ['ADMIN'],
  'league-admins.create': ['ADMIN'],
  'league-admins.edit': ['ADMIN'],
  'league-admins.delete': ['ADMIN'],

  // My League panel (ADMIN_LEAGUE only)
  'my-league.view': ['ADMIN_LEAGUE'],
};

// Маппинг разделов сайдбара к разрешениям
export const sidebarPermissions: Record<string, Permission> = {
  'teams': 'teams.view',
  'players': 'players.view',
  'matches': 'matches.view',
  'tournaments': 'tournaments.view',
  'tournamentCategories': 'tournament-categories.view',
  'sportClubs': 'sport-clubs.view',
  'sportTypes': 'sport-types.view',
  'match-participants': 'match-participants.view',
  'users': 'users.view',
  'playgrounds': 'playgrounds.view',
  'achievements': 'achievements.view',
  'regions': 'regions.view',
  'cities': 'cities.view',
  'countries': 'countries.view',
  'files': 'files.view',
  'news': 'news.view',
  'favorites': 'favorites.view',
  'permissions': 'permissions.view',
  'leagueAdmins': 'league-admins.view',
  'myLeague': 'my-league.view',
  'createLeague': 'my-league.view',
  'createDivision': 'my-league.view',
  'createLeagueTeam': 'my-league.view',
  'createLeaguePlayer': 'my-league.view',
};

/**
 * Проверяет, есть ли у пользователя определенное разрешение
 */
export function hasPermission(userRoles: UserRole[], permission: Permission): boolean {
  if (!userRoles || userRoles.length === 0) {
    return false;
  }
  
  const allowedRoles = permissions[permission];
  if (!allowedRoles) {
    return false;
  }
  
  return userRoles.some(role => allowedRoles.includes(role.name as RoleName));
}

/**
 * Проверяет, может ли пользователь видеть определенный раздел в сайдбаре
 */
export function canAccessSidebarItem(userRoles: UserRole[], sidebarItemId: string): boolean {
  const permission = sidebarPermissions[sidebarItemId];
  if (!permission) {
    return false;
  }
  
  return hasPermission(userRoles, permission);
}

/**
 * Получает все разрешения пользователя
 */
export function getUserPermissions(userRoles: UserRole[]): Permission[] {
  const userPermissions: Permission[] = [];
  
  Object.entries(permissions).forEach(([permission, allowedRoles]) => {
    if (userRoles.some(role => allowedRoles.includes(role.name as RoleName))) {
      userPermissions.push(permission as Permission);
    }
  });
  
  return userPermissions;
}

/**
 * Проверяет, имеет ли пользователь хотя бы одну из ролей
 */
export function hasAnyRole(userRoles: UserRole[], requiredRoles: RoleName[]): boolean {
  return userRoles.some(role => requiredRoles.includes(role.name as RoleName));
}

/**
 * Проверяет, имеет ли пользователь все указанные роли
 */
export function hasAllRoles(userRoles: UserRole[], requiredRoles: RoleName[]): boolean {
  return requiredRoles.every(requiredRole => 
    userRoles.some(role => role.name === requiredRole)
  );
}

export function normalizeUserRoles(user: any): UserRole[] {
  if (!user) {
    return [];
  }

  // Если roles это массив
  if (Array.isArray(user.roles) && user.roles.length > 0) {
    // Проверяем первый элемент - если это объект с name, возвращаем как есть
    if (typeof user.roles[0] === 'object' && user.roles[0] !== null && 'name' in user.roles[0]) {
      return user.roles as UserRole[];
    }
    // Если это массив строк (mobile auth case), конвертируем в UserRole[]
    if (typeof user.roles[0] === 'string') {
      return user.roles.map((roleName: string, index: number) => ({
        id: index + 1,
        name: roleName,
      }));
    }
  }

  // Если role это массив строк
  if (Array.isArray(user.role)) {
    return user.role
      .filter((roleName): roleName is string => typeof roleName === 'string' && roleName.trim().length > 0)
      .map((roleName, index) => ({
        id: index + 1,
        name: roleName,
      }));
  }

  // Если role это строка
  if (typeof user.role === 'string' && user.role.trim().length > 0) {
    return [{ id: 1, name: user.role }];
  }

  return [];
}

export function canAccessAdminDashboard(userRoles: UserRole[]): boolean {
  return hasAnyRole(userRoles, dashboardAccessRoles);
}
